import React, { useState, useEffect } from 'react';
import _ from 'lodash';

import { connect } from 'react-redux';
import { BaseStore } from 'src/store';
import { ProductsDetailsStore } from '../../store/types';
import { Row, Col } from 'antd';
import { rowDefaultProps } from 'src/utils/grid';

import {
  // LayoutDynamicZoneComponentProps,
  LayoutCardSize,
  LayoutDynamicZone,
} from 'src/modules/layout';
import { ProductsDetailsOverlay, ProductDetailsSlider } from 'src/modules/products';
import { AssetImageSubtype } from 'src/modules/assets';
import { ProductsDetailsPropertyMenu } from 'src/modules/products/details';
import LayoutPage from 'src/modules/layout/components/page/LayoutPage';
import { OutputChannels, useLocale } from 'src/modules/locale';
import { ProductsListingOverview } from 'src/modules/products';
import { FormattedMessage, useIntl } from 'react-intl';
import { GlobalFormSalesforce, GlobalSpin } from 'src/modules/global';
import styles from './ProductsDetailsPage.module.scss';
import { Button } from 'src/modules/global/components/button/Button';
import DesignerTeaser from 'src/modules/global/components/designerTeaser/DesignerTeaser';
import { useMetaTitle } from 'src/utils/ssgHelpers';
import cleanupProducts from 'src/utils/cleanupProducts';

interface ProductsDetailsPageProps extends ProductsDetailsStore {}

const filterProductsByOutputChannel = (products: any, channel: OutputChannels) => {
  if (!channel) return [];
  return cleanupProducts(products, channel);
};

const ProductsDetailsPage: React.FunctionComponent<ProductsDetailsPageProps> = (
  props: ProductsDetailsPageProps
) => {
  const intl = useIntl();
  const { getLocalizedObject, outputChannel } = useLocale();
  const {
    productDetailsPage,
    relatedProducts,
    isRelatedProductsLoading,
    ...localizedProps
  } = getLocalizedObject(props);

  const productSeriesProducts = filterProductsByOutputChannel(
    localizedProps.productSeriesProducts,
    outputChannel
  );

  const product = productDetailsPage?.product;
  const designer = product?.product_series?.designer;

  // Force redraw since on SSG the content language (ex: description) doesn't update
  const [forceRedrawKey, setForceRedraKey] = useState('pdp-unmounted');
  useEffect(() => {
    setForceRedraKey('pdp-mounted');
  }, []);

  // Select either content relation similar_products or default logic relatedProducts
  // https://www.notion.so/Manual-selection-of-Similar-products-9447223457b540c78e1015ddf55c1b7d
  let selectedRelatedProducts = !_.isEmpty(product?.similar_products)
    ? product?.similar_products
    : relatedProducts;

  selectedRelatedProducts = _.sortBy(selectedRelatedProducts, (p) => p?.ranking);

  const breadcrumbs =
    product && product?.product_categories?.[0]
      ? [
          {
            title: intl.formatMessage({
              id: 'breadcrumbs/products',
            }),
            as: '/products',
            href: '/products',
          },
          {
            title: product.product_categories[0].name,
            as: `/products/${encodeURIComponent(product.product_categories[0].slug)}`,
            href: '/products/[category]',
          },
          {
            title: product.name,
            as: `/products/${encodeURIComponent(
              product.product_categories[0].slug
            )}/${encodeURIComponent(product.slug)}`,
            href: '/products/[category]/[slug]',
          },
        ]
      : [];

  const metaTitleByProductName = useMetaTitle(product?.name);
  const metaTitle = product?.meta_title || metaTitleByProductName;

  if (!product?.name) return null;

  const { name, product_series, description, dynamic_content, meta_description } = product;

  const cutoutImages = filterProductsByOutputChannel(
    product.assets.filter((asset) => asset.subtype === AssetImageSubtype.Cutout),
    outputChannel
  );

  return (
    <LayoutPage
      key={forceRedrawKey}
      title={metaTitle}
      description={meta_description || description}
      metaImage={cutoutImages[0]?.file.url}
      breadcrumbs={breadcrumbs}
      topPadding={false}>
      <div className={styles.container}>
        <section className={styles.block}>
          <Row {...rowDefaultProps} className={styles.container}>
            <Col
              {...{ xs: { span: 24, order: 2 }, md: { span: 12, order: 1 } }}
              className={styles.details}>
              <Row {...rowDefaultProps}>
                <Col {...{ xs: { span: 24 }, md: { span: 20 } }}>
                  <h3>{name}.</h3>
                  <div className={styles.details}>
                    <div className={styles.subtitle}>
                      {product_series?.designer && (
                        <div>
                          <FormattedMessage id="page/products/details/designer" /> {designer?.name}.
                        </div>
                      )}
                      {/* <div>{pim_id}</div> */}
                    </div>
                    <div
                      className={styles.description}
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  </div>
                </Col>
              </Row>
              <div className={styles.buttonRow}>
                <Row {...rowDefaultProps}>
                  <Col xs={12} sm={12} md={10}>
                    <GlobalFormSalesforce
                      additionalFormFields={{
                        '00N3X00000LwRSe': name,
                      }}
                      title={intl.formatMessage({ id: 'form/title' })}
                      buttonText={intl.formatMessage({ id: 'page/products/details/inquire' })}
                      topJsx={
                        <div>
                          <span className={styles.selectedProductLabel}>
                            {intl.formatMessage({
                              id: 'page/products/details/inquire/selected_product',
                            })}
                          </span>
                          <h3 className={styles.selectedProductName}>{name}</h3>
                          <div className={styles.thinLine} />
                        </div>
                      }
                    />
                  </Col>
                  {outputChannel !== OutputChannels.US && (
                    <Col xs={12} sm={12} md={10}>
                      <Button
                        type="secondary"
                        widthType="fullWidth"
                        href="/store"
                        as="/store"
                        target="_blank"
                        rel="noopener noreferer">
                        <FormattedMessage id="page/products/details/store" />
                      </Button>
                    </Col>
                  )}
                  <Col xs={24} sm={24} md={20} lg={20}>
                    <ProductsDetailsPropertyMenu className={styles.colorsContainer} />
                  </Col>
                </Row>
              </div>
            </Col>
            <Col {...{ xs: { span: 24, order: 1 }, md: { span: 12, order: 2 } }}>
              <ProductDetailsSlider
                className={styles.productDetailsSlider}
                activeAsset={cutoutImages[0]}
                name={name}
                assets={cutoutImages}
              />
            </Col>
            <ProductsDetailsOverlay />
          </Row>
        </section>
        {dynamic_content && (
          <section className={styles.block}>
            <LayoutDynamicZone components={dynamic_content} />
          </section>
        )}
        {product_series?.designer && <DesignerTeaser designer={designer} />}
        {!!isRelatedProductsLoading ? (
          <GlobalSpin />
        ) : (
          !!productSeriesProducts?.length && (
            <section className={styles.block}>
              <ProductsListingOverview
                size={LayoutCardSize.Small}
                headingTitle="page/products/details/section_family_subheading"
                headingMore={product.product_series.plp_display}
                values={{ name: product.product_series.name }}
                link={'/products/family/' + product.product_series.slug}
                relatedOverview={true}
                products={productSeriesProducts}
              />
            </section>
          )
        )}
        {!!selectedRelatedProducts.length && !!productSeriesProducts?.length && (
          <div className={styles.separator} />
        )}
        {!!selectedRelatedProducts.length && (
          <section className={styles.block}>
            <ProductsListingOverview
              size={LayoutCardSize.Small}
              headingTitle="page/products/details/section_related_subheading"
              headingMore={false}
              relatedOverview={true}
              products={selectedRelatedProducts}
            />
          </section>
        )}
      </div>
    </LayoutPage>
  );
};

const mapStateToProps = (state: BaseStore) => {
  return { ...state.products.details };
};

export default connect(mapStateToProps)(ProductsDetailsPage);
